import { Link } from "@inertiajs/react"
import {
  BarChart3,
  Briefcase,
  Building2,
  LayoutDashboard,
  MailPlus,
  ShieldBan,
  UserCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { NavMain } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  adminAgenciesPath,
  adminAnalyticsPath,
  adminBlocklistEntriesPath,
  adminBrandsPath,
  adminCreatorsPath,
  adminHomePath,
  invitationsPath,
} from "@/routes"
import type { NavItem } from "@/types"

import AppLogo from "./app-logo"

/**
 * AdminSidebar — sidebar for the OKS Admin shell.
 *
 * No brand chrome. NavUser lives in the header right slot like the brand shell.
 *
 * Nav item definitions live INSIDE the component body so every title can
 * flow through `useTranslation()` — keeps EN strings out of the component file.
 *
 * OKS-7: routes to `adminHomePath()` directly rather than the legacy
 * /dashboard dispatcher — the admin sidebar only ever serves admin users.
 *
 * OKS-15: extended with the admin-console navigation. Five new entries
 * (Overview / Brands / Agencies / Creators / Blocklist) sit alongside the
 * OKS-11 Invitations entry. Icons picked with UX Lead — `shield-ban` for
 * Blocklist (vs the row-flag's `shield-alert`) keeps the nav distinct from
 * the per-creator blocklist indicator. The OKS-7 placeholder card grid
 * that previously rendered at `/admin` is replaced by the proper
 * `Admin/Overview` page in the same ticket.
 *
 * Active-state styling comes "for free" from the underlying `<NavMain>` →
 * `<SidebarMenuButton isActive={page.url.startsWith(item.href)}>` pattern,
 * which matches the `TbjCW` Pencil component (background `#FFFFFF14`,
 * foreground `$accent-primary`).
 */
export function AdminSidebar() {
  const { t } = useTranslation()

  // OKS-11: OKS Admin can invite into any tenant (see
  // `InvitationPolicy#create_any?`). The Invitations entry is always
  // present on the admin shell — no tenancy gate.
  const adminNavItems: NavItem[] = [
    {
      title: t("admin_nav.overview"),
      href: adminHomePath(),
      icon: LayoutDashboard,
      "data-testid": "admin-sidebar-nav-overview",
    },
    // OKS-66 — Platform analytics. Sits between Overview and Brands per
    // UX Spec §1 (operator mental model: landing → telemetry → tenant
    // lists → guardrails). Admin-only by inheritance — the entire
    // `AdminSidebar` is mounted only for admin-role users via `AppLayout`.
    {
      title: t("admin_nav.analytics"),
      href: adminAnalyticsPath(),
      icon: BarChart3,
      "data-testid": "admin-sidebar-nav-analytics",
    },
    {
      title: t("admin_nav.brands"),
      href: adminBrandsPath(),
      icon: Building2,
      "data-testid": "admin-sidebar-nav-brands",
    },
    {
      title: t("admin_nav.agencies"),
      href: adminAgenciesPath(),
      icon: Briefcase,
      "data-testid": "admin-sidebar-nav-agencies",
    },
    {
      title: t("admin_nav.creators"),
      href: adminCreatorsPath(),
      icon: UserCircle,
      "data-testid": "admin-sidebar-nav-creators",
    },
    {
      title: t("admin_nav.blocklist"),
      href: adminBlocklistEntriesPath(),
      icon: ShieldBan,
      "data-testid": "admin-sidebar-nav-blocklist",
    },
    {
      title: t("nav.invitations"),
      href: invitationsPath(),
      icon: MailPlus,
    },
  ]

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href={adminHomePath()} prefetch>
                <AppLogo />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={adminNavItems} />
      </SidebarContent>
    </Sidebar>
  )
}
